import { useState } from 'react'
import { LoginPrompt } from '@/features/Authentication/components/LoginPrompt'
import { SpinnerIcon } from '@/components/ui/icons'

export interface AuthLoadingOverlayProps {
  isLoading: boolean
  message?: string
}


export function AuthLoadingOverlay({ isLoading, message = 'Signing in...' }: AuthLoadingOverlayProps) {
  if (!isLoading) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="bg-white dark:bg-surface-elevated rounded-2xl p-6 shadow-elevated flex flex-col items-center gap-3">
        <SpinnerIcon className="size-8 text-brand-primary" />
        <p className="font-medium text-text-primary">{message}</p>
      </div>
    </div>
  )
}

export interface AuthWrapperProps {
  isAuthenticated: boolean
  authLoading: boolean
  onGoogleLogin: () => void
  onAppleLogin: () => void
  onPhoneLogin: () => void
  onGuestContinue: () => void
  children: React.ReactNode
  /** If true, shows auth prompt when not authenticated */
  requireAuth?: boolean
  /** Custom message to show when prompting for auth */
  authPromptMessage?: string
}

export function AuthWrapper({
  isAuthenticated,
  authLoading,
  onGoogleLogin,
  onAppleLogin,
  onPhoneLogin,
  onGuestContinue,
  children,
  requireAuth = false,
}: AuthWrapperProps) {
  const [guestMode, setGuestMode] = useState(false)

  const handleGuestContinue = () => {
    setGuestMode(true)
    onGuestContinue()
  }

  // Show login prompt if auth is required and user is not authenticated and not in guest mode
  const showLoginPrompt = requireAuth && !isAuthenticated && !guestMode

  return (
    <>
      {showLoginPrompt ? (
        <LoginPrompt
          onGoogleLogin={onGoogleLogin}
          onAppleLogin={onAppleLogin}
          onPhoneLogin={onPhoneLogin}
          onSkip={handleGuestContinue}
        />
      ) : (
        children
      )}

      <AuthLoadingOverlay isLoading={authLoading} />
    </>
  )
}

export interface UseAuthGuestModeReturn {
  guestMode: boolean
  setGuestMode: (value: boolean) => void
  showLoginPrompt: (query: string, isAuthenticated: boolean) => boolean
  handleGuestContinue: () => void
}

/**
 * Hook to manage guest mode state and authentication prompts
 * 
 * @param queryLength - Current search query length (for conditional display)
 * @param isAuthenticated - Whether user is authenticated
 * @returns Guest mode state and helper functions
 */
export function useAuthGuestMode(): UseAuthGuestModeReturn {
  const [guestMode, setGuestMode] = useState(false)

  const showLoginPrompt = (query: string, isAuthenticated: boolean) => {
    return query.length < 3 && !isAuthenticated && !guestMode
  }

  const handleGuestContinue = () => {
    setGuestMode(true)
  }

  return {
    guestMode,
    setGuestMode,
    showLoginPrompt,
    handleGuestContinue,
  }
}

// Re-export LoginPrompt for direct use if needed
export { LoginPrompt } from '@/features/Authentication/components/LoginPrompt'
